// src/pages/invoicing/create-pi-items.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link, useParams } from "react-router-dom";
import Select from "react-select";
import API_BASE_URL from "../../config";
import { Modal, Button } from "react-bootstrap";

const EditPIItems = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [brands, setBrands] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [billing, setBilling] = useState(null);
  const [missingLeadFields, setMissingLeadFields] = useState({
    state: false,
  });
  const [piHeader, setPiHeader] = useState(null);

  // === Fetch the full lead (profile_pic, funnel_stage, priority, etc.) ===
  const fetchLeadById = async (leadId) => {
    if (!leadId) return null;

    // Try common endpoints you likely have. Keep both; whichever returns success first is used.
    const endpoints = [
      `${API_BASE_URL}/leads/get_lead/${leadId}`,
      `${API_BASE_URL}/crm/get_lead_by_id/${leadId}`,
    ];

    for (const url of endpoints) {
      try {
        const res = await fetch(url, { credentials: "include" });
        const data = await res.json();
        if (data?.status === "success" && (data.lead || data.data)) {
          return data.lead || data.data; // normalize possible shapes
        }
      } catch (e) {
        // keep trying next endpoint
      }
    }
    return null;
  };

  const buildLeadFromHeader = (hdr) => ({
    id: hdr.lead_id ?? null,
    full_name: hdr.to_company || hdr.company_name || "",
    company: hdr.to_company || "",
    address: hdr.to_address || "",
    city: hdr.to_city || "",
    state: hdr.to_state || "",
    pincode: hdr.to_pincode || "",
    gst_number: hdr.to_gstin || "",
    email: hdr.to_email || "",
    alt_email: hdr.to_alt_email || "",
    mobile: hdr.to_mobile || "",
    alt_mobile: hdr.to_alt_mobile || "",
    profile_pic: null,
    assigned_to_name: hdr.assigned_to_name || "",
    created_by_name: hdr.created_by_name || "",
    created_on: hdr.created_on || hdr.created_at || null,
  });

  // Make any lead payload look like the one used on the Create page
  const normalizeLead = (raw = {}) => ({
    id: raw.id ?? raw.lead_id ?? null,
    full_name: raw.full_name ?? raw.name ?? raw.contact_name ?? "",
    designation: raw.designation ?? "",
    company: raw.company ?? raw.company_name ?? "",
    mobile: raw.mobile ?? raw.phone ?? "",
    alt_mobile: raw.alt_mobile ?? raw.alt_phone ?? "",
    email: raw.email ?? "",
    alt_email: raw.alt_email ?? "",
    funnel_stage: raw.funnel_stage ?? "",
    priority: raw.priority ?? "",
    assigned_to_name: raw.assigned_to_name ?? raw.assigned_to ?? "",
    created_by_name: raw.created_by_name ?? raw.added_by ?? "",
    created_on: raw.created_on ?? raw.created_at ?? null,
    profile_pic: raw.profile_pic ?? "",
    address: raw.address ?? "",
    city: raw.city ?? "",
    state: raw.state ?? "",
    pincode: raw.pincode ?? "",
    gst_number: raw.gst_number ?? raw.gst ?? raw.gstin ?? "",
  });

  useEffect(() => {
    if (!piHeader || !brands.length) return;

    const found = brands.find(
      (b) => String(b.id) === String(piHeader.brand_id),
    );

    setSelectedBrand(
      found
        ? {
            value: found.id,
            label: found.brand_name,
            logo: found.logo,
            color: found.color_code,
          }
        : null,
    );
  }, [brands, piHeader]);

  useEffect(() => {
    document.title = "Edit Proforma - Line Items | Webyappy";

    // 🔹 Load selected lead from localStorage (ONLY for NEW; skip in EDIT)
    if (!id) {
      const saved = localStorage.getItem("selected_proforma_lead");
      try {
        if (saved && saved !== "undefined") {
          const parsed = JSON.parse(saved);
          setLead(parsed);
          setMissingLeadFields({
            state: !parsed.state || parsed.state.trim() === "",
          });
        } else {
          console.warn("No valid lead found in localStorage.");
        }
      } catch (err) {
        console.error("Invalid JSON in selected_proforma_lead:", err);
      }
    }

    // 🔹 Fetch Brands
    fetch(`${API_BASE_URL}/invoicing/get_brands`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setBrands(data.brands);
        } else {
          console.warn("Failed to load brands");
        }
      })
      .catch((err) => {
        console.error("Error fetching brands:", err);
      });

    // 🔹 Fetch Billing Address
    fetch(`${API_BASE_URL}/invoicing/get_billing_address`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setBilling(data.billing_address);
        } else {
          console.warn("No billing address found");
        }
      })
      .catch((err) => {
        console.error("Error fetching billing address:", err);
      });
    // 🔹 Load existing PI header
    fetch(`${API_BASE_URL}/invoicing/get_pi/${id}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.status !== "success") {
          alert("Proforma not found");
          return;
        }

        const invoice = data.invoice;
        setPiHeader(invoice);

        // Only store what the PI header actually has
        const hdrLead = buildLeadFromHeader(invoice);
        setLead(hdrLead);

        setMissingLeadFields({
          state: !hdrLead.state || String(hdrLead.state).trim() === "",
        });
      })
      .catch((e) => console.error(e));

    // 🔹 Load existing PI items
    fetch(`${API_BASE_URL}/invoicing/get_pi_items/${id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success") {
          setLineItems(
            (data.items || []).map((it) => ({
              sac: String(it.sac_code ?? "998314"),
              title: String(it.title ?? ""),
              gst: Number(it.gst_percent ?? 18),
              amount:
                it.amount === null || it.amount === undefined
                  ? ""
                  : String(it.amount),
            })),
          );
          if (!data.items || data.items.length === 0) {
            setLineItems([{ sac: "998314", title: "", gst: 18, amount: "" }]);
          }
        }
      })
      .catch((e) => console.error(e));
  }, []);

  const [lineItems, setLineItems] = useState([
    { sac: "998314", title: "", gst: 18, amount: "" },
  ]);

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { sac: "998314", title: "", gst: 18, amount: "" },
    ]);
  };

  const removeLineItem = (index) => {
    const updatedItems = [...lineItems];
    updatedItems.splice(index, 1);
    setLineItems(updatedItems);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      return sum + amt;
    }, 0);
  };

  const calculateTotalGST = () => {
    return lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      const gst = parseFloat(item.gst || 0);
      return sum + (amt * gst) / 100;
    }, 0);
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + calculateTotalGST();
  };

  const handleSubmitInvoice = async () => {
    if (!lead) return;
    if (missingLeadFields.state) return;

    // Brand required for both create & update
    if (!selectedBrand) {
      alert("Please select a brand before submitting.");
      return;
    }

    // Validate line items
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.title || item.title.trim() === "") {
        alert(`Please enter title for item ${i + 1}`);
        return;
      }
      if (!item.amount || isNaN(item.amount) || parseFloat(item.amount) <= 0) {
        alert(`Please enter valid amount for item ${i + 1}`);
        return;
      }
      if (item.gst === "" || isNaN(item.gst)) {
        alert(`Please enter GST% for item ${i + 1}`);
        return;
      }
      if (!item.sac || item.sac.trim() === "") {
        alert(`Please enter SAC code for item ${i + 1}`);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      let pi_id_local = id; // if editing, id exists from URL

      if (!pi_id_local) {
        // -------- CREATE MODE --------
        const createResponse = await fetch(
          `${API_BASE_URL}/invoicing/create_proforma_invoice`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              company_name: lead.company,
              address: `${lead.address}, ${lead.city}, ${lead.state} - ${lead.pincode}`,
              brand_id: selectedBrand?.value || null,
              to_company: lead.company,
              to_address: lead.address,
              to_city: lead.city,
              to_state: lead.state,
              to_pincode: lead.pincode,
              to_gstin: lead.gst_number ?? lead.gstNumber ?? lead.gstin ?? "",
            }),
          },
        );
        const createData = await createResponse.json();
        if (createData.status !== "success") {
          alert("Failed to create proforma invoice.");
          setIsSubmitting(false);
          return;
        }
        pi_id_local = createData.pi_id;
      } else {
        // -------- UPDATE MODE --------
        // 1) Update header
        const updHeader = await fetch(
          `${API_BASE_URL}/invoicing/update_proforma_invoice`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              pi_id: pi_id_local,
              brand_id: selectedBrand?.value || null,
              to_company: lead.company,
              to_address: lead.address,
              to_city: lead.city,
              to_state: lead.state,
              to_pincode: lead.pincode,
              to_gstin: lead.gst_number ?? lead.gstNumber ?? lead.gstin ?? "",
            }),
          },
        );
        const updData = await updHeader.json();
        if (updData.status !== "success") {
          alert("Failed to update proforma header.");
          setIsSubmitting(false);
          return;
        }
      }

      // SAVE/REPLACE items (works for both modes)
      const itemsToSave = lineItems.map((item) => ({
        pi_id: pi_id_local,
        title: item.title,
        sac_code: item.sac,
        gst_percent: item.gst,
        amount: item.amount,
      }));

      const itemsEndpoint = id
        ? "replace_proforma_line_items"
        : "add_proforma_line_items";

      const saveItemsRes = await fetch(
        `${API_BASE_URL}/invoicing/${itemsEndpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(itemsToSave),
        },
      );
      const saveItemsData = await saveItemsRes.json();
      if (saveItemsData.status !== "success") {
        alert("Failed to save line items.");
        setIsSubmitting(false);
        return;
      }

      // Compute totals on client
      const subtotal = calculateSubtotal();
      const totalGST = calculateTotalGST();
      let cgst = 0,
        sgst = 0,
        igst = 0;
      if (
        billing?.state?.toLowerCase().trim() ===
        lead?.state?.toLowerCase().trim()
      ) {
        cgst = (totalGST / 2).toFixed(2);
        sgst = (totalGST / 2).toFixed(2);
      } else {
        igst = totalGST.toFixed(2);
      }

      const totals = {
        pi_id: pi_id_local,
        subtotal,
        cgst,
        sgst,
        igst,
        grand_total: subtotal + totalGST,
      };

      const updTotals = await fetch(
        `${API_BASE_URL}/invoicing/update_proforma_totals`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(totals),
        },
      );
      const totalsData = await updTotals.json();
      if (totalsData.status !== "success") {
        alert("Failed to update totals.");
        setIsSubmitting(false);
        return;
      }

      // Redirect to view
      window.location.href = `/pages/invoicing/pi-view/${pi_id_local}`;
    } catch (err) {
      console.error("Error submitting invoice:", err);
      alert("Something went wrong.");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          <div className="row">
            {/* 📇 Left: Lead Card */}
            {lead ? (
              <div className="col-12 col-lg-4 mb-4">
                <h6 className="font-weight-bold text-dark mb-3">
                  Company Details:
                </h6>

                <div className="card card-custom shadow-sm px-4 py-4 position-relative h-100 w-100">
                  {/* Eye icon only if we know the lead_id from header */}
                  {piHeader?.lead_id && (
                    <div
                      className="position-absolute"
                      style={{ top: "1rem", right: "1rem" }}
                    >
                      <a
                        href={`/pages/crm/ViewLeadD/${piHeader.lead_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="far fa-eye text-danger icon-lg"></i>
                      </a>
                    </div>
                  )}

                  {/* Company name */}
                  <div className="mb-2">
                    <h5
                      className="text-dark font-weight-bold mb-1"
                      style={{ fontSize: "15px" }}
                    >
                      {lead.company || "—"}
                    </h5>
                  </div>

                  {/* Address */}
                  <div className="text-dark mb-3" style={{ fontSize: "13px" }}>
                    {lead.address || lead.city || lead.state || lead.pincode ? (
                      <>
                        <div>{lead.address || "—"}</div>
                        <div>
                          {lead.city || "—"}
                          {lead.city && (lead.state || lead.pincode)
                            ? ", "
                            : ""}
                          {lead.state || ""}
                          {lead.state && lead.pincode ? " - " : ""}
                          {lead.pincode || ""}
                        </div>
                      </>
                    ) : (
                      <div>—</div>
                    )}
                  </div>

                  {/* GSTIN */}
                  <div className="text-dark" style={{ fontSize: "13px" }}>
                    <strong>GSTIN:</strong> {lead.gst_number || "—"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-12 col-lg-4 mb-4">
                <div className="card card-custom shadow-sm p-4 text-center text-muted">
                  No details found.
                </div>
              </div>
            )}

            {/* 📦 Right: Item Entry */}
            <div
              className="col-12 col-lg-8 mb-4"
              style={{ marginTop: window.innerWidth < 992 ? "2rem" : "0rem" }}
            >
              <h6 className="font-weight-bold text-dark mb-3">
                Proforma Item Entry
              </h6>
              <div className="card card-custom shadow-sm p-4">
                {/* Brand Select */}
                <div className="form-group">
                  <label className="small">
                    Brand<span className="text-danger">*</span>
                  </label>
                  <Select
                    className={`basic-single shadow-sm ${!selectedBrand && isSubmitting ? "border border-danger" : ""}`}
                    classNamePrefix="select"
                    options={brands.map((b) => ({
                      label: b.brand_name,
                      value: b.id,
                      logo: b.logo,
                      color: b.color_code,
                    }))}
                    placeholder="Select Brand"
                    isClearable
                    value={selectedBrand}
                    onChange={(selected) => setSelectedBrand(selected)}
                    formatOptionLabel={({ label, logo, color }) => (
                      <div className="d-flex align-items-center">
                        <div
                          className="border mr-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginRight: "10px",
                          }}
                        >
                          <img
                            src={
                              logo?.startsWith("http")
                                ? logo
                                : `${API_BASE_URL.replace("/index.php", "")}/${logo}`
                            }
                            alt={label}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block",
                              padding: "4px",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            color,
                            fontSize: "14px",
                            fontWeight: "Bold",
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    )}
                  />
                </div>

                {/* Repeater rows */}
                {lineItems.map((item, index) => (
                  <div className="row mb-3 mt-3" key={index}>
                    <div className="col-sm-6 col-md-1 form-group mb-2">
                      <label className="small font-weight-bold">Sr. No.</label>
                      <input
                        type="text"
                        className="form-control"
                        value={index + 1}
                        readOnly
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">SAC</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.sac}
                        onChange={(e) =>
                          handleLineItemChange(index, "sac", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-5 form-group mb-1">
                      <label className="small font-weight-bold">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Service Title"
                        value={item.title}
                        onChange={(e) =>
                          handleLineItemChange(index, "title", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">GST%</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.gst}
                        onChange={(e) =>
                          handleLineItemChange(index, "gst", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // ✅ Allow digits with optional decimal (max 2 decimals)
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            handleLineItemChange(index, "amount", value);
                          }
                        }}
                      />
                    </div>

                    <div
                      className="col-md-1 d-flex align-items-end"
                      style={{ paddingBottom: "12px" }}
                    >
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add Item Button */}
                <div className="text-right mb-4">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={addLineItem}
                  >
                    <i className="fas fa-plus-circle mr-1"></i> Add Item
                  </button>
                </div>

                {/* Total Summary */}
                <div className="mt-4 border-top pt-4 text-right">
                  <div>
                    <strong>Subtotal:</strong> ₹{calculateSubtotal().toFixed(2)}
                  </div>
                  <div>
                    <strong>GST:</strong> ₹{calculateTotalGST().toFixed(2)}
                  </div>
                  <div className="font-weight-bold">
                    <strong>Grand Total:</strong> ₹
                    {calculateGrandTotal().toFixed(2)}
                  </div>
                </div>

                <div className="text-center mt-4">
                  {billing?.state && lead?.state && (
                    <div className="text-muted mt-2 text-right small">
                      Billing State: <strong>{billing.state}</strong> | Lead
                      State: <strong>{lead.state}</strong> —{" "}
                      {billing.state.toLowerCase() === lead.state.toLowerCase()
                        ? "CGST + SGST will apply"
                        : "IGST will apply"}
                    </div>
                  )}

                  <Button
                    className="btn btn-primary px-5 mt-3"
                    disabled={isSubmitting || missingLeadFields.state}
                    onClick={handleSubmitInvoice}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" />
                        {id ? "Updating..." : "Submitting..."}
                      </>
                    ) : (
                      <>{id ? "Update Invoice" : "Submit Invoice"}</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPIItems;
